import React from 'react';
import { useForm } from 'react-hook-form';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import capitalize from 'lodash/capitalize';
import ControlledCheckbox from '@/components/common/form/ControlledCheckbox';
import OpportunityStatus from '@/constants/OpportunityStatus';
import translate from '@/lib/intl/translate';

type ConstituentOpportunitiesStatusSelectProps = {
  statusFilters: Record<OpportunityStatus, boolean>;
  onSubmit: React.Dispatch<
    React.SetStateAction<Record<OpportunityStatus, boolean>>
  >;
};

function ConstituentOpportunitiesStatusSelect({
  statusFilters,
  onSubmit,
}: ConstituentOpportunitiesStatusSelectProps) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { handleSubmit, control } = useForm({
    defaultValues: statusFilters,
  });

  const activeFilterCount = React.useMemo(
    () => Object.values(statusFilters).filter((val) => val).length,
    [statusFilters],
  );

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onFormSubmit = (formData: Record<OpportunityStatus, boolean>) => {
    onSubmit(formData);
    setAnchorEl(null);
  };

  return (
    <div>
      <Button
        aria-controls={open ? 'opportunities-status-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleOpen}
        endIcon={open ? <ExpandLess /> : <ExpandMore />}
        classes={{
          text: 'text-text-primary',
          endIcon: 'ml-0',
          root: 'hover:bg-grey-100 child:pl-1',
        }}
      >
        {translate('OPPORTUNITIES_STATUS', { activeFilterCount })}
      </Button>
      <Popover
        id="opportunities-status-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <div className="w-72 py-3 px-3">
            {Object.values(OpportunityStatus).map((item) => (
              <div className="w-full" key={item}>
                <ControlledCheckbox
                  control={control}
                  label={capitalize(item)}
                  name={item}
                />
              </div>
            ))}
          </div>
          <div className="flex justify-end border-t border-grey-300 p-4">
            <Button
              className="w-full text-base sm:w-auto"
              disableElevation
              onClick={handleClose}
            >
              {translate('CANCEL')}
            </Button>
            <Button type="submit" variant="contained">
              {translate('SUBMIT')}
            </Button>
          </div>
        </form>
      </Popover>
    </div>
  );
}

export default ConstituentOpportunitiesStatusSelect;
